import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "sc.compare";
const EVT = "sc:compare-changed";

function load(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function save(ids: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    window.dispatchEvent(new CustomEvent(EVT));
  } catch {
    /* ignore */
  }
}

export function useCompare() {
  const [ids, setIds] = useState<string[]>(() => load());

  useEffect(() => {
    const onChange = () => setIds(load());
    window.addEventListener(EVT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(EVT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const toggle = useCallback((id: string) => {
    const cur = load();
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id].slice(0, 4);
    save(next);
  }, []);

  const remove = useCallback((id: string) => {
    save(load().filter((x) => x !== id));
  }, []);

  const clear = useCallback(() => save([]), []);

  return { ids, toggle, remove, clear, has: (id: string) => ids.includes(id) };
}
